import React, {
  createContext,
  Dispatch,
  MutableRefObject,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  CreateDataRoomFileDocument,
  CreateDataRoomFileMutation,
  CreateDataRoomFileMutationVariables,
  CreateDataRoomFileQuestionDocument,
  CreateDataRoomFileQuestionMutation,
  CreateDataRoomFileQuestionMutationVariables,
  CreateDataRoomFolderDocument,
  CreateDataRoomFolderMutation,
  CreateDataRoomFolderMutationVariables,
  S3MultipartUploadCompleteDocument,
  S3MultipartUploadCompleteMutation,
  S3MultipartUploadCompleteMutationVariables,
} from "@/src/graphql/generated";
import useGqlClient from "@/src/hooks/useGqlClient";
import { useInvalidateQueryKeys } from "@/src/hooks/useInvalidateQueryKeys";
import { getNumberOfParts, Uploader } from "@/src/utils/multipartUploader";
import { useHistory } from "react-router-dom";
import { fileWrapper, folderWrapper } from "./NewFilesUploader";

export function updateFolderRecursively(
  folders: folderWrapper[],
  targetFolderId: number,
  updater: (folder: folderWrapper) => folderWrapper,
): folderWrapper[] {
  return folders.map((folder) => {
    if (folder.id === targetFolderId) {
      return updater(folder);
    }

    if (folder.children && folder.children.length > 0) {
      return {
        ...folder,
        children: updateFolderRecursively(
          folder.children,
          targetFolderId,
          updater,
        ),
      };
    }

    return folder;
  });
}

export function getAllFilesFromFolders(folders: folderWrapper[]): fileWrapper[] {
  const files: fileWrapper[] = [];
  for (const folder of folders) {
    if (folder.files) {
      files.push(...folder.files);
    }
    if (folder.children) {
      files.push(...getAllFilesFromFolders(folder.children));
    }
  }
  return files;
}

export function countUploadItems(
  files: fileWrapper[],
  folders: folderWrapper[],
): { total: number; completed: number; errored: number; active: number } {
  const allFiles = [...files, ...getAllFilesFromFolders(folders)];
  const total = allFiles.length + folders.length;
  const completed =
    allFiles.filter((f) => f.status === "complete").length +
    folders.filter((f) => f.status === "complete").length;
  const errored =
    allFiles.filter((f) => f.status === "error" || f.status === "cancelled")
      .length +
    folders.filter((f) => f.status === "error" || f.status === "cancelled")
      .length;
  const active = allFiles.filter(
    (f) =>
      f.status === "uploading" ||
      f.status === "creating" ||
      f.status === "pending",
  ).length;
  return { total, completed, errored, active };
}

export function hasPendingUploads(
  files: fileWrapper[],
  folders: folderWrapper[],
): boolean {
  const allFiles = [...files, ...getAllFilesFromFolders(folders)];
  return (
    allFiles.some(
      (f) =>
        f.status !== "complete" &&
        f.status !== "error" &&
        f.status !== "cancelled",
    ) ||
    folders.some(
      (f) =>
        f.status !== "complete" &&
        f.status !== "error" &&
        f.status !== "cancelled",
    )
  );
}

/** True only once upload work has started — excludes modal "pending" queue items. */
export function hasActiveUploads(
  files: fileWrapper[],
  folders: folderWrapper[],
): boolean {
  const allFiles = [...files, ...getAllFilesFromFolders(folders)];
  return (
    allFiles.some((f) => f.status === "uploading" || f.status === "creating") ||
    folders.some((f) => f.status === "uploading" || f.status === "creating")
  );
}

/** Drop the underlying blob bytes while keeping name/type for UI. */
function releaseFileBlob(file: File): File {
  if (file.size === 0) return file;
  return new File([], file.name, { type: file.type });
}

function withReleasedBlob(
  file: fileWrapper,
  updates: Partial<fileWrapper>,
): fileWrapper {
  const next = { ...file, ...updates };
  if (next.status === "complete" || next.status === "cancelled") {
    next.file = releaseFileBlob(next.file);
  }
  return next;
}

function releaseBlobsInFolder(folder: folderWrapper): folderWrapper {
  return {
    ...folder,
    files: folder.files?.map((f) =>
      f.status === "complete" || f.status === "cancelled"
        ? { ...f, file: releaseFileBlob(f.file) }
        : f,
    ),
    children: folder.children?.map(releaseBlobsInFolder),
  };
}

function applyProgressUpdates(
  files: fileWrapper[],
  folders: folderWrapper[],
  progressById: Map<string, number>,
): { files: fileWrapper[]; folders: folderWrapper[]; changed: boolean } {
  let changed = false;

  const nextFiles = files.map((f) => {
    const pct = progressById.get(f.id);
    if (pct === undefined || f.uploadProgress === pct) return f;
    changed = true;
    return { ...f, uploadProgress: pct };
  });

  const mapFolder = (folder: folderWrapper): folderWrapper => {
    let folderChanged = false;
    const nextFolderFiles = folder.files?.map((f) => {
      const pct = progressById.get(f.id);
      if (pct === undefined || f.uploadProgress === pct) return f;
      folderChanged = true;
      return { ...f, uploadProgress: pct };
    });
    const nextChildren = folder.children?.map(mapFolder);
    if (nextChildren) {
      for (let i = 0; i < nextChildren.length; i++) {
        if (nextChildren[i] !== folder.children![i]) {
          folderChanged = true;
          break;
        }
      }
    }
    if (!folderChanged) return folder;
    changed = true;
    return {
      ...folder,
      files: nextFolderFiles,
      children: nextChildren,
    };
  };

  const nextFolders = folders.map(mapFolder);
  return {
    files: changed ? nextFiles : files,
    folders: changed ? nextFolders : folders,
    changed,
  };
}

type StartUploadsOptions = {
  dataRoomFolderId?: string;
  questionId?: string;
  targetFolderName?: string;
  /** When provided, use these instead of reading from state (avoids setState race) */
  files?: fileWrapper[];
  folders?: folderWrapper[];
};

type UploadContextType = {
  uploadFiles: fileWrapper[];
  setUploadFiles: Dispatch<SetStateAction<fileWrapper[]>>;
  uploadFolders: folderWrapper[];
  setUploadFolders: Dispatch<SetStateAction<folderWrapper[]>>;
  updateFile: (
    id: string,
    fileWrap: fileWrapper,
    updates: Partial<fileWrapper>,
  ) => void;
  updateFolderRecursively: typeof updateFolderRecursively;
  isUploading: boolean;
  targetFolderId: string | null;
  targetFolderName: string | null;
  setTargetFolderId: (id: string | null) => void;
  setTargetFolderName: (name: string | null) => void;
  uploadModalOpen: boolean;
  setUploadModalOpen: (open: boolean) => void;
  enqueueFiles: (files: fileWrapper[], folders: folderWrapper[]) => void;
  startUploads: (opts: StartUploadsOptions) => void;
  uploadFile: (
    fileWrap: fileWrapper,
    opts?: { dataRoomFolderId?: string; questionId?: string },
  ) => Promise<void>;
  uploadFolder: (
    folderWrap: folderWrapper,
    parentFolderId?: string,
    opts?: { dataRoomFolderId?: string },
  ) => Promise<void>;
  cancelUploads: () => void;
  clearQueue: () => void;
  clearCompleted: () => void;
};

/** Stable action surface — reading this does not subscribe to progress ticks. */
export type UploadApi = Pick<
  UploadContextType,
  | "enqueueFiles"
  | "startUploads"
  | "setTargetFolderId"
  | "setTargetFolderName"
  | "setUploadModalOpen"
  | "clearCompleted"
  | "clearQueue"
  | "cancelUploads"
>;

const UploadContext = createContext<UploadContextType | undefined>(undefined);
const UploadApiRefContext = createContext<MutableRefObject<UploadApi> | null>(
  null,
);

export function UploadContextProvider({
  children,
  navigationScopePath,
}: {
  children: React.ReactNode;
  /** Allow SPA navigation within this route subtree while uploads continue. */
  navigationScopePath?: string;
}) {
  const [uploadFiles, setUploadFiles] = useState<fileWrapper[]>([]);
  const [uploadFolders, setUploadFolders] = useState<folderWrapper[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [targetFolderId, setTargetFolderId] = useState<string | null>(null);
  const [targetFolderName, setTargetFolderName] = useState<string | null>(null);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);

  const client = useGqlClient();
  const history = useHistory();
  const {
    invalidateDataRoomFolders,
    invalidateTransactionFiles,
    invalidateDealActivities,
  } = useInvalidateQueryKeys();

  const uploadersRef = useRef<Map<string, Uploader>>(new Map());
  const cancelledRef = useRef(false);
  const targetRef = useRef<{
    dataRoomFolderId?: string;
    questionId?: string;
  }>({});
  const uploadFilesRef = useRef(uploadFiles);
  const uploadFoldersRef = useRef(uploadFolders);
  const isUploadingRef = useRef(isUploading);
  const pendingProgressRef = useRef<Map<string, number>>(new Map());
  const lastFlushedProgressRef = useRef<Map<string, number>>(new Map());
  const progressFlushRafRef = useRef<number | null>(null);
  const apiRef = useRef<UploadApi>({
    enqueueFiles: () => {},
    startUploads: () => {},
    setTargetFolderId: () => {},
    setTargetFolderName: () => {},
    setUploadModalOpen: () => {},
    clearCompleted: () => {},
    clearQueue: () => {},
    cancelUploads: () => {},
  });

  useEffect(() => {
    uploadFilesRef.current = uploadFiles;
  }, [uploadFiles]);

  useEffect(() => {
    uploadFoldersRef.current = uploadFolders;
  }, [uploadFolders]);

  useEffect(() => {
    isUploadingRef.current = isUploading;
  }, [isUploading]);

  const invalidateCaches = useCallback(() => {
    invalidateDataRoomFolders();
    invalidateTransactionFiles();
    invalidateDealActivities();
  }, [
    invalidateDataRoomFolders,
    invalidateTransactionFiles,
    invalidateDealActivities,
  ]);

  // Per-file completion used to invalidate on every file — that refetches the
  // whole data room repeatedly during bulk uploads and balloons memory.
  const invalidateTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scheduleInvalidateCaches = useCallback(() => {
    if (invalidateTimerRef.current != null) {
      clearTimeout(invalidateTimerRef.current);
    }
    invalidateTimerRef.current = setTimeout(() => {
      invalidateTimerRef.current = null;
      invalidateCaches();
    }, 750);
  }, [invalidateCaches]);
  const flushInvalidateCaches = useCallback(() => {
    if (invalidateTimerRef.current != null) {
      clearTimeout(invalidateTimerRef.current);
      invalidateTimerRef.current = null;
    }
    invalidateCaches();
  }, [invalidateCaches]);

  const flushProgressUpdates = useCallback(() => {
    progressFlushRafRef.current = null;
    const pending = pendingProgressRef.current;
    if (pending.size === 0) return;

    const toApply = new Map<string, number>();
    pending.forEach((pct, id) => {
      if (lastFlushedProgressRef.current.get(id) !== pct) {
        toApply.set(id, pct);
      }
    });
    pendingProgressRef.current = new Map();
    if (toApply.size === 0) return;

    toApply.forEach((pct, id) => {
      lastFlushedProgressRef.current.set(id, pct);
    });

    const applied = applyProgressUpdates(
      uploadFilesRef.current,
      uploadFoldersRef.current,
      toApply,
    );
    if (!applied.changed) return;

    if (applied.files !== uploadFilesRef.current) {
      setUploadFiles(applied.files);
    }
    if (applied.folders !== uploadFoldersRef.current) {
      setUploadFolders(applied.folders);
    }
  }, []);

  const scheduleProgressUpdate = useCallback(
    (id: string, percentage: number) => {
      if (lastFlushedProgressRef.current.get(id) === percentage) {
        return;
      }
      pendingProgressRef.current.set(id, percentage);
      if (progressFlushRafRef.current != null) return;
      progressFlushRafRef.current = requestAnimationFrame(flushProgressUpdates);
    },
    [flushProgressUpdates],
  );

  const updateFile = useCallback(
    (id: string, fileWrap: fileWrapper, updates: Partial<fileWrapper>) => {
      // Progress-only updates are batched via rAF to avoid setState storms.
      if (
        updates.uploadProgress !== undefined &&
        updates.status === undefined &&
        Object.keys(updates).length === 1
      ) {
        scheduleProgressUpdate(id, updates.uploadProgress);
        return;
      }

      if (fileWrap.parentFolder) {
        setUploadFolders((prev) =>
          updateFolderRecursively(
            prev,
            fileWrap.parentFolder!.id,
            (folder) => ({
              ...folder,
              files: folder.files?.map((file) =>
                file.id === id ? withReleasedBlob(file, updates) : file,
              ),
            }),
          ),
        );
        return;
      }

      setUploadFiles((prev) =>
        prev.map((f) => (f.id === id ? withReleasedBlob(f, updates) : f)),
      );
    },
    [scheduleProgressUpdate],
  );

  useEffect(() => {
    return () => {
      if (progressFlushRafRef.current != null) {
        cancelAnimationFrame(progressFlushRafRef.current);
      }
    };
  }, []);

  const uploadFile = useCallback(
    (
      fileWrap: fileWrapper,
      opts?: { dataRoomFolderId?: string; questionId?: string },
    ): Promise<void> => {
      return new Promise((resolve) => {
        if (cancelledRef.current) {
          updateFile(fileWrap.id, fileWrap, { status: "cancelled" });
          resolve();
          return;
        }

        const dataRoomFolderId =
          opts?.dataRoomFolderId ?? targetRef.current.dataRoomFolderId;
        const questionId = opts?.questionId ?? targetRef.current.questionId;

        updateFile(fileWrap.id, fileWrap, { status: "uploading" });

        const fileParams = {
          name: fileWrap.name,
          description: "",
          dataRoomPermissionID: fileWrap.selectedPermission.value as string,
          fileName: fileWrap.file.name,
          fileType: fileWrap.file.type,
          parts: getNumberOfParts(fileWrap.file),
        };

        const request = questionId
          ? client.request<
              CreateDataRoomFileQuestionMutation,
              CreateDataRoomFileQuestionMutationVariables
            >(CreateDataRoomFileQuestionDocument, {
              ...fileParams,
              questionID: questionId,
              permissionIDs: [],
            })
          : client.request<
              CreateDataRoomFileMutation,
              CreateDataRoomFileMutationVariables
            >(CreateDataRoomFileDocument, {
              ...fileParams,
              dataRoomFolderID:
                fileWrap.selectedDataRoomFolderID || dataRoomFolderId || "",
              permissionIDs: fileWrap.selectedPermission.value
                ? [fileWrap.selectedPermission.value]
                : [],
            });

        request
          .catch(() => {
            updateFile(fileWrap.id, fileWrap, { status: "error" });
            resolve();
          })
          .then((data) => {
            if (!data) {
              return;
            }

            if (cancelledRef.current) {
              updateFile(fileWrap.id, fileWrap, { status: "cancelled" });
              resolve();
              return;
            }

            updateFile(fileWrap.id, fileWrap, { status: "uploading" });

            const response = questionId
              ? (data as CreateDataRoomFileQuestionMutation)
                  .createDataRoomFileQuestion
              : (data as CreateDataRoomFileMutation).createDataRoomFile;

            const uploader = new Uploader({
              file: fileWrap.file,
              uploadId: response.multiPartUploadID,
              parts: response.parts.map((p) => ({
                PartNumber: p.partNumber,
                signedUrl: p.presignedUrl,
              })),
            });

            uploadersRef.current.set(fileWrap.id, uploader);
            uploader.start();

            uploader.onError(() => {
              uploadersRef.current.delete(fileWrap.id);
              updateFile(fileWrap.id, fileWrap, {
                status: cancelledRef.current ? "cancelled" : "error",
              });
              resolve();
            });

            uploader.onProgress((progress) => {
              updateFile(fileWrap.id, fileWrap, {
                uploadProgress: progress.percentage,
              });
            });

            uploader.onComplete((uploadedParts) => {
              client
                .request<
                  S3MultipartUploadCompleteMutation,
                  S3MultipartUploadCompleteMutationVariables
                >(S3MultipartUploadCompleteDocument, {
                  dataRoomFileID: response.dataRoomFile.id,
                  dataRoomFileVersionID: response.initialVersion.id,
                  multiPartUploadID: response.multiPartUploadID,
                  parts: uploadedParts.map((up) => ({
                    partNumber: up.PartNumber,
                    etag: up.ETag,
                  })),
                })
                .then((d) => {
                  uploadersRef.current.delete(fileWrap.id);
                  if (!d) {
                    updateFile(fileWrap.id, fileWrap, { status: "error" });
                    resolve();
                    return;
                  }
                  updateFile(fileWrap.id, fileWrap, {
                    status: "complete",
                    createdDataRoomFileId: response.dataRoomFile.id,
                  });
                  scheduleInvalidateCaches();
                  resolve();
                })
                .catch(() => {
                  uploadersRef.current.delete(fileWrap.id);
                  updateFile(fileWrap.id, fileWrap, { status: "error" });
                  resolve();
                });
            });
          });
      });
    },
    [client, scheduleInvalidateCaches, updateFile],
  );

  const uploadFolder = useCallback(
    (
      folderWrap: folderWrapper,
      parentFolderId?: string,
      opts?: { dataRoomFolderId?: string },
    ): Promise<void> => {
      return new Promise((resolve, reject) => {
        if (cancelledRef.current) {
          setUploadFolders((prev) =>
            updateFolderRecursively(prev, folderWrap.id, (folder) => ({
              ...folder,
              status: "cancelled",
            })),
          );
          resolve();
          return;
        }

        const parentID =
          parentFolderId ||
          opts?.dataRoomFolderId ||
          targetRef.current.dataRoomFolderId ||
          "";

        setUploadFolders((prev) =>
          updateFolderRecursively(prev, folderWrap.id, (folder) => ({
            ...folder,
            status: "uploading",
            files: folder.files?.map((file) => ({
              ...file,
              status: "uploading",
            })),
          })),
        );

        client
          .request<
            CreateDataRoomFolderMutation,
            CreateDataRoomFolderMutationVariables
          >(CreateDataRoomFolderDocument, {
            name: folderWrap.name,
            dataRoomFolderID: parentID,
            permissionIDs: folderWrap.selectedPermission?.value
              ? [folderWrap.selectedPermission.value]
              : [],
          })
          .then(async (folderData) => {
            if (!folderData) {
              throw new Error("Folder creation failed.");
            }
            const newFolderId = folderData.createDataRoomFolder.id;

            for (const childFolder of folderWrap.children || []) {
              await uploadFolder(childFolder, newFolderId, opts);
            }

            const files = folderWrap.files || [];
            for (let i = 0; i < files.length; i += 5) {
              if (cancelledRef.current) break;
              const batch = files.slice(i, i + 5);
              await Promise.all(
                batch.map((fileWrap) => {
                  const updatedFileWrap = {
                    ...fileWrap,
                    selectedDataRoomFolderID: newFolderId,
                  };
                  return uploadFile(updatedFileWrap, opts);
                }),
              );
            }
          })
          .then(() => {
            setUploadFolders((prev) =>
              updateFolderRecursively(prev, folderWrap.id, (folder) => ({
                ...folder,
                status: cancelledRef.current ? "cancelled" : "complete",
              })),
            );
            scheduleInvalidateCaches();
            resolve();
          })
          .catch((err) => {
            setUploadFolders((prev) =>
              updateFolderRecursively(prev, folderWrap.id, (folder) => ({
                ...folder,
                status: "error",
              })),
            );
            reject(err);
          });
      });
    },
    [client, scheduleInvalidateCaches, uploadFile],
  );

  const startUploads = useCallback(
    (opts: StartUploadsOptions) => {
      cancelledRef.current = false;
      targetRef.current = {
        dataRoomFolderId: opts.dataRoomFolderId,
        questionId: opts.questionId,
      };
      if (opts.dataRoomFolderId) {
        setTargetFolderId(opts.dataRoomFolderId);
      }
      if (opts.targetFolderName) {
        setTargetFolderName(opts.targetFolderName);
      }
      setIsUploading(true);

      const run = async () => {
        try {
          const sourceFolders = opts.folders ?? uploadFoldersRef.current;
          const sourceFiles = opts.files ?? uploadFilesRef.current;
          const folders = sourceFolders.filter(
            (f) => f.status === "pending" || f.status === "error",
          );
          const files = sourceFiles.filter(
            (f) => f.status === "pending" || f.status === "error",
          );

          for (const folder of folders) {
            if (cancelledRef.current) break;
            await uploadFolder(folder, undefined, {
              dataRoomFolderId: opts.dataRoomFolderId,
            });
          }

          for (let i = 0; i < files.length; i += 5) {
            if (cancelledRef.current) break;
            const batch = files.slice(i, i + 5);
            await Promise.all(
              batch.map((f) =>
                uploadFile(f, {
                  dataRoomFolderId: opts.dataRoomFolderId,
                  questionId: opts.questionId,
                }),
              ),
            );
          }
        } catch (error) {
          console.error("Error during file operations:", error);
        } finally {
          setIsUploading(false);
          flushInvalidateCaches();
        }
      };

      void run();
    },
    [uploadFile, uploadFolder, flushInvalidateCaches],
  );

  const cancelUploads = useCallback(() => {
    cancelledRef.current = true;
    uploadersRef.current.forEach((uploader) => uploader.abort());
    uploadersRef.current.clear();
    pendingProgressRef.current.clear();
    lastFlushedProgressRef.current.clear();

    setUploadFiles((prev) =>
      prev.map((f) =>
        f.status === "pending" ||
        f.status === "uploading" ||
        f.status === "creating"
          ? withReleasedBlob(f, { status: "cancelled" })
          : f,
      ),
    );
    setUploadFolders((prev) =>
      prev.map(function markCancelled(folder): folderWrapper {
        const next: folderWrapper = {
          ...folder,
          status:
            folder.status === "pending" ||
            folder.status === "uploading" ||
            folder.status === "creating"
              ? "cancelled"
              : folder.status,
          files: folder.files?.map((f) =>
            f.status === "pending" ||
            f.status === "uploading" ||
            f.status === "creating"
              ? withReleasedBlob(f, { status: "cancelled" })
              : f,
          ),
          children: folder.children?.map(markCancelled),
        };
        return releaseBlobsInFolder(next);
      }),
    );
    setIsUploading(false);
  }, []);

  const clearQueue = useCallback(() => {
    pendingProgressRef.current.clear();
    lastFlushedProgressRef.current.clear();
    setUploadFiles([]);
    setUploadFolders([]);
  }, []);

  const clearCompleted = useCallback(() => {
    setUploadFiles((prev) =>
      prev.filter(
        (f) =>
          f.status !== "complete" &&
          f.status !== "error" &&
          f.status !== "cancelled",
      ),
    );
    setUploadFolders((prev) =>
      prev.filter(
        (f) =>
          f.status !== "complete" &&
          f.status !== "error" &&
          f.status !== "cancelled",
      ),
    );
  }, []);

  const enqueueFiles = useCallback(
    (files: fileWrapper[], folders: folderWrapper[]) => {
      setUploadFiles((prev) => [...prev, ...files]);
      setUploadFolders((prev) => [...prev, ...folders]);
    },
    [],
  );

  apiRef.current = {
    enqueueFiles,
    startUploads,
    setTargetFolderId,
    setTargetFolderName,
    setUploadModalOpen,
    clearCompleted,
    clearQueue,
    cancelUploads,
  };

  // Warn on browser refresh/close, and on SPA nav that leaves this upload
  // context, while uploads are actively in flight (not merely queued).
  // Uses refs so progress ticks don't re-bind beforeunload / history.block.
  useEffect(() => {
    const warningMessage =
      "You have uploads in progress. Leaving now may leave them incomplete. Are you sure you want to leave?";

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (
        !hasActiveUploads(uploadFilesRef.current, uploadFoldersRef.current) &&
        !isUploadingRef.current
      ) {
        return;
      }
      e.preventDefault();
      e.returnValue = warningMessage;
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    const unblock = navigationScopePath
      ? history.block((location) => {
          if (
            !hasActiveUploads(
              uploadFilesRef.current,
              uploadFoldersRef.current,
            ) &&
            !isUploadingRef.current
          ) {
            return undefined;
          }
          if (
            location.pathname === navigationScopePath ||
            location.pathname.startsWith(`${navigationScopePath}/`)
          ) {
            return undefined;
          }
          return warningMessage;
        })
      : () => {};

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      unblock();
    };
  }, [history, navigationScopePath]);

  const contextValue = useMemo<UploadContextType>(
    () => ({
      uploadFiles,
      setUploadFiles,
      uploadFolders,
      setUploadFolders,
      updateFile,
      updateFolderRecursively,
      isUploading,
      targetFolderId,
      targetFolderName,
      setTargetFolderId,
      setTargetFolderName,
      uploadModalOpen,
      setUploadModalOpen,
      enqueueFiles,
      startUploads,
      uploadFile,
      uploadFolder,
      cancelUploads,
      clearQueue,
      clearCompleted,
    }),
    [
      uploadFiles,
      uploadFolders,
      updateFile,
      isUploading,
      targetFolderId,
      targetFolderName,
      uploadModalOpen,
      enqueueFiles,
      startUploads,
      uploadFile,
      uploadFolder,
      cancelUploads,
      clearQueue,
      clearCompleted,
    ],
  );

  return (
    <UploadApiRefContext.Provider value={apiRef}>
      <UploadContext.Provider value={contextValue}>
        {children}
      </UploadContext.Provider>
    </UploadApiRefContext.Provider>
  );
}

export const useUploadContext = () => {
  const context = useContext(UploadContext);
  if (!context) {
    throw new Error(
      "useUploadContext must be used within a UploadContextProvider",
    );
  }
  return context;
};

/**
 * Upload actions without subscribing to file/progress state.
 * Use this from folder/tree views so progress ticks don't re-render the list.
 */
export function useUploadApi(): UploadApi {
  const apiRef = useContext(UploadApiRefContext);
  if (!apiRef) {
    throw new Error("useUploadApi must be used within a UploadContextProvider");
  }
  return useMemo(
    () => ({
      enqueueFiles: (files, folders) =>
        apiRef.current.enqueueFiles(files, folders),
      startUploads: (opts) => apiRef.current.startUploads(opts),
      setTargetFolderId: (id) => apiRef.current.setTargetFolderId(id),
      setTargetFolderName: (name) => apiRef.current.setTargetFolderName(name),
      setUploadModalOpen: (open) => apiRef.current.setUploadModalOpen(open),
      clearCompleted: () => apiRef.current.clearCompleted(),
      clearQueue: () => apiRef.current.clearQueue(),
      cancelUploads: () => apiRef.current.cancelUploads(),
    }),
    [apiRef],
  );
}
